import { type ApolloError } from "@apollo/client"
import { Box, Button, Flex, FormLabel, Input, Radio, RadioGroup, Text, Textarea, useToast } from "@chakra-ui/react"
import { useAtom } from "jotai"
import React, { useEffect, useState, type ChangeEvent } from "react"
import { userDetailsAtom } from "../../../components/Menu/menu.store"
import { useEditFmpProductEntryMutation, useViewFmpProductLazyQuery } from "../../../type-and-hooks"

interface EditIndividualRowProps {
  // Define your prop types here
  onClose: () => void
  fmpId: number | undefined
  fmpProductId: string | undefined
  onUpdateSuccess: () => void
}

const EditIndividualRow: React.FC<EditIndividualRowProps> = ({ onClose, fmpId, fmpProductId, onUpdateSuccess }) => {
  const [fetchFMPProduct, { data: fmpProductData }] = useViewFmpProductLazyQuery({
    fetchPolicy: "no-cache"
  })
  const [userDetails] = useAtom(userDetailsAtom)
  const userId = userDetails?._id
  const [systemFRL, setSystemFRL] = useState<string>()
  interface SystemService {
    service_category_name: string
    service_size: string
  }
  interface SystemData {
    project_system_ref_value?: string
    project_system_desc?: string
    barrier_type_name?: string
    barrier_thickness_mm?: string
    barrier_construction_name?: string
    fire_stopping_system_manufacturer?: string
    fire_stopping_system_type?: string
    fire_stopping_system_name?: string
    fmp_product_services?: SystemService[] | undefined
  }

  const [systemData, setSystemData] = useState<SystemData>({})

  useEffect(() => {
    const userId = userDetails?._id
    if (userId && fmpId && fmpProductId) {
      fetchFMPProduct({ variables: { fmpProductId: Number(fmpProductId), fmpProjectId: fmpId, userId: Number(userId) } })
    }
  }, [fmpId, fmpProductId, fetchFMPProduct, userDetails])

  useEffect(() => {
    if (fmpProductData?.viewFMPProduct) {
      // Parse the JSON string if it's a string, otherwise use as is
      const parsedData = typeof fmpProductData.viewFMPProduct === "string" ? JSON.parse(fmpProductData.viewFMPProduct) : fmpProductData.viewFMPProduct

      setSystemData(parsedData ?? {})
      setSystemFRL(parsedData?.system_frl)
    }
  }, [fmpProductData])

  const [updateFMPProject] = useEditFmpProductEntryMutation({
    onCompleted: data => {
      // Check if the mutation returned null (indicating an error)
      if (data?.editFMPProductEntry === null) {
        // This means there was an error, but it's not being caught by onError
        // We need to handle this case manually
        return
      }

      onClose()
      toast({
        title: "FMP Project updated successfully",
        status: "success",
        duration: 5000,
        isClosable: true
      })
      onUpdateSuccess()
    }
  })
  const toast = useToast()

  // Remove the useErrorToast hook since it's causing issues
  // useErrorToast({
  //   toast,
  //   error: updateError,
  //   errorMessage: "Error updating FMP Project"
  // })

  // Add proper error handling function
  const handleError = (error: ApolloError | Error): void => {
    let errorMessage = "Failed to update FMP Project"

    // Check for the errors array structure (like in your response)
    if ("errors" in error && Array.isArray(error.errors) && error.errors.length > 0) {
      errorMessage = error.errors[0]
    } else if ("graphQLErrors" in error && error.graphQLErrors?.length > 0) {
      // Cast the GraphQL error as a string, following the pattern used throughout the codebase
      errorMessage = (error.graphQLErrors[0] as unknown as string) || error.graphQLErrors[0].message || "An error occurred"
    } else if ("message" in error) {
      errorMessage = error.message
    }

    toast({
      title: "Error updating FMP Project",
      description: errorMessage,
      status: "error",
      duration: 5000,
      isClosable: true
    })
  }

  const handleSubmit = (): void => {
    if (!fmpId || !userId || !fmpProductId) return
    void updateFMPProject({
      variables: {
        input: {
          fmp_project_id: fmpId,
          fmp_product_id: Number(fmpProductId),
          system_frl: systemFRL,
          project_system_ref: systemData.project_system_ref_value,
          project_system_desc: systemData.project_system_desc,
          user_id: Number(userId)
        }
      }
    }).catch(handleError)
  }
  const handleFRLChange = ({ currentTarget }: ChangeEvent<HTMLInputElement>, index: number): void => {
    const newFRL = systemFRL?.split("/")
    let frl = ""
    if (newFRL && index === 1) {
      frl = `-/${currentTarget.value}/${newFRL[2]}`
    }
    if (newFRL && index === 2) {
      frl = `-/${newFRL[1]}/${currentTarget.value}`
    }
    setSystemFRL(frl)
    setSystemData(prev => ({ ...prev, system_frl: frl }))
  }

  const handleSystemData = ({ currentTarget }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const stateName = currentTarget.getAttribute("name") as string
    setSystemData(prev => ({ ...prev, [stateName]: currentTarget.value }))
  }

  return (
    <Box margin="auto">
      {/* Your component implementation here */}
      <Box display="grid" gridTemplateColumns="104px 1fr 1fr" gap="16px" alignItems="flex-start">
        <Box gridColumn="span 3" textAlign="center" fontWeight="bold">
          Edit System
        </Box>
        {/* project System Reference */}
        <>
          <Text variant="RBodyBold">Project System Ref.</Text>
          <Box display="grid" gap="6px" alignItems="flex-start">
            <FormLabel m="0" p="0" fontSize="16px">
              Project System Reference*{" "}
            </FormLabel>
            <Input
              color="black"
              type="text"
              bg="white"
              borderRadius="0"
              value={systemData.project_system_ref_value}
              onChange={handleSystemData}
              name="project_system_ref_value"
            />
          </Box>
          <Box display="grid" gap="6px">
            <FormLabel m="0" p="0" fontSize="16px">
              Description
            </FormLabel>
            <Textarea
              color="black"
              bg="white"
              borderRadius="0"
              value={systemData.project_system_desc}
              name="project_system_desc"
              onChange={handleSystemData}
            />
          </Box>
        </>
        <Box bg="white" height="1px" gridColumn="span 3" />
        {/* Barrier Type */}
        <>
          <Text variant="RBodyBold">Barrier Type</Text>
          <Box display="grid" gap="6px" alignItems="flex-start">
            <FormLabel m="0" p="0" fontSize="16px">
              Type
            </FormLabel>
            <Input color="grey" type="text" bg="white" borderRadius="0" readOnly value={systemData.barrier_type_name} />
          </Box>
          <Box display="grid" gap="6px">
            <FormLabel m="0" p="0" fontSize="16px">
              Thickness
            </FormLabel>
            <Input color="grey" type="text" w="60px" bg="white" borderRadius="0" readOnly value={systemData.barrier_thickness_mm} />
          </Box>
        </>
        <Box bg="white" height="1px" gridColumn="span 3" />

        {/* Barrier construction */}
        <>
          <Text variant="RBodyBold">Barrier Construction</Text>
          <Box display="grid" gap="6px" alignItems="flex-start">
            <FormLabel m="0" p="0" fontSize="16px">
              Type
            </FormLabel>
            <Input type="text" color="grey" bg="white" borderRadius="0" readOnly value={systemData.barrier_construction_name} />
          </Box>
          <Box></Box>
        </>
        <Box bg="white" height="1px" gridColumn="span 3" />
        {/* System FRL */}
        <>
          <Text variant="RBodyBold">System FRL</Text>
          <Box display="grid" gap="6px" alignItems="center" gridTemplateColumns="200px 20px 1fr" w="100%" gridColumn={"span 2"}>
            <RadioGroup onChange={setSystemFRL} value={systemFRL} display={"flex"} gap="16px">
              <Box>
                <Radio value="-/30/30">-/30/30</Radio>
                <Radio value="-/60/60">-/60/60</Radio>
                <Radio value="-/90/90">-/90/90</Radio>
              </Box>
              <Box>
                <Radio value="-/120/120">-/120/120</Radio>
                <Radio value="-/180/180">-/180/180</Radio>
                <Radio value="-/240/240">-/240/240</Radio>
              </Box>
            </RadioGroup>
            <Box>or</Box>
            <Box display="flex" alignItems="center" justifyContent="flex-start" gap="4px" w="100%" alignSelf="center">
              <Text>-/</Text>
              <Input
                onChange={e => {
                  handleFRLChange(e, 1)
                }}
                color="black"
                type="number"
                w="51px"
                bg="white"
                borderRadius="0"
                px="1"
                textAlign="center"
                value={systemFRL?.split("/")[1]}
              />
              <Text>-/</Text>
              <Input
                onChange={e => {
                  handleFRLChange(e, 2)
                }}
                color="black"
                type="number"
                w="51px"
                bg="white"
                borderRadius="0"
                px="1"
                textAlign="center"
                value={systemFRL?.split("/")[2]}
              />
            </Box>
          </Box>
        </>

        {/* Services penetrating */}
        {systemData.fmp_product_services && (
          <>
            {/* <Text variant="RBodyBold">Services Penetrating</Text> */}
            {Object.entries(systemData.fmp_product_services).map(([key, val]) => (
              <>
                {key === "0" && <Text variant="RBodyBold">Services Penetrating</Text>}
                {key !== "0" && <Text variant="RBodyBold"></Text>}
                <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gridColumn="span 2" gap="16px">
                  <Box display="grid" gap="6px" alignItems="flex-start">
                    <FormLabel m="0" p="0" fontSize="16px">
                      Service
                    </FormLabel>
                    <Input color="grey" type="text" bg="white" borderRadius="0" readOnly value={val.service_category_name} />
                  </Box>
                  <Box display="grid" gridTemplateColumns="1fr min-content" gap="6px">
                    <Box>
                      <FormLabel m="0" p="0" fontSize="16px">
                        Service Size
                      </FormLabel>
                      <Input color="grey" type="text" bg="white" borderRadius="0" readOnly value={val.service_size} />
                    </Box>
                    <Box alignContent="center" pt="4">
                      or
                    </Box>
                  </Box>
                  <Box>
                    <FormLabel m="0" p="0" fontSize="16px">
                      Application
                    </FormLabel>
                    <Input color="grey" type="text" bg="white" readOnly borderRadius="0" />
                  </Box>
                </Box>
              </>
            ))}
          </>
        )}
        <Box bg="white" height="1px" gridColumn="span 3" />
        {/* File stopping system */}
        <>
          <Text variant="RBodyBold">File stopping system</Text>

          <Box display="grid" gap="6px" alignItems="flex-start">
            <FormLabel m="0" p="0" fontSize="16px">
              Manufacturer
            </FormLabel>
            <Input color="grey" type="text" bg="white" borderRadius="0" readOnly value={systemData.fire_stopping_system_manufacturer} />
          </Box>
          <Box height="100%" justifyContent="space-between" display="grid" gridTemplateColumns="repeat(2, 1fr)" gap="16px">
            <Box display="grid" gap="6px">
              <FormLabel m="0" p="0" fontSize="16px">
                ProductType
              </FormLabel>
              <Input color="grey" type="text" bg="white" borderRadius="0" readOnly value={systemData.fire_stopping_system_type} />
            </Box>
            <Box display="grid" gap="6px">
              <FormLabel m="0" p="0" fontSize="16px">
                Product
              </FormLabel>
              <Input color="grey" type="text" bg="white" borderRadius="0" readOnly value={systemData.fire_stopping_system_name} />
            </Box>
          </Box>
        </>
      </Box>
      <Box mt="4" gridColumn="span 3" display="grid" gap="16px">
        <Flex gap="16px" alignItems="center" justifyContent="center">
          <Button variant="linkButton" color="white" fontWeight="normal" onClick={onClose}>
            Cancel
          </Button>
          <Button borderWidth="1px" borderColor="primary" variant="Button1RegularOutline" onClick={handleSubmit}>
            Update
          </Button>
        </Flex>
      </Box>
    </Box>
  )
}

export default EditIndividualRow
